/**
 * Axios Configuration con Interceptor per Multi-Currency
 *
 * Questo file configura axios per:
 * 1. Inviare l'header X-Currency con la valuta selezionata dall'utente
 * 2. Il backend convertirà i prezzi in base all'header X-Currency
 * 3. Quando cambia valuta, la pagina si ricarica per applicare la nuova valuta
 *
 * IMPORTANTE: Questo file deve essere importato in _app.tsx prima di ogni altra cosa
 * per garantire che tutti gli axios.get/post/etc usino gli interceptor configurati.
 */

import axios from "axios";
import {
    DEFAULT_CURRENCY,
    CURRENCY_STORAGE_KEY,
    isValidCurrency,
} from "@/models/currency";

/**
 * Ottiene la valuta corrente da localStorage
 */
const getCurrentCurrency = (): string => {
    if (typeof window === "undefined") {
        return DEFAULT_CURRENCY;
    }

    try {
        const savedCurrency = localStorage.getItem(CURRENCY_STORAGE_KEY);
        if (savedCurrency && isValidCurrency(savedCurrency)) {
            return savedCurrency;
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error reading currency from localStorage:", error);
    }

    return DEFAULT_CURRENCY;
};

/**
 * Configura gli interceptor axios per gestire la valuta
 *
 * Invia la valuta corrente selezionata dall'utente nell'header X-Currency
 * Il backend convertirà i prezzi in base a questo header
 */
export const setupAxiosInterceptors = () => {
    // REQUEST INTERCEPTOR: Aggiunge header X-Currency con la valuta corrente
    axios.interceptors.request.use(
        config => {
            // Leggi la valuta corrente da localStorage
            const currentCurrency = getCurrentCurrency();
            // Axios interceptors modificano direttamente il config (pattern standard)
            // eslint-disable-next-line no-param-reassign
            config.headers = config.headers || {};
            // eslint-disable-next-line no-param-reassign
            config.headers["X-Currency"] = currentCurrency;
            return config;
        },
        error => {
            return Promise.reject(error);
        }
    );

    // RESPONSE INTERCEPTOR: Gestisce errori generici
    axios.interceptors.response.use(
        response => {
            // Risposta OK, ritorna normalmente
            // Il backend ha già convertito i prezzi in base all'header X-Currency
            return response;
        },
        error => {
            // Gestione errori generica (se necessario)
            return Promise.reject(error);
        }
    );

    // Log iniziale (solo una volta all'avvio)
    if (process.env.NODE_ENV === "development") {
        const currentCurrency = getCurrentCurrency();
        // eslint-disable-next-line no-console
        console.log(
            `✅ Axios interceptors configured - sends X-Currency: ${currentCurrency}`
        );
    }
};

/**
 * Utility per aggiornare manualmente l'header X-Currency in una richiesta specifica
 * (raramente necessario, solo se vuoi override per una chiamata specifica)
 */
export const withCurrency = (
    config: { headers?: Record<string, string> },
    currency: string
) => {
    return {
        ...config,
        headers: {
            ...config?.headers,
            "X-Currency": currency,
        },
    };
};

export default setupAxiosInterceptors;
